import {
  machineIdentsFromPlan,
  productRowsFromPlan,
} from "./serviceOrderProducts";

function splitNames(value) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function observationFarmNames(obs) {
  if (!obs) return [];
  const names = splitNames(obs.service_farm_names || obs.site_farm_name);
  return Array.from(new Set(names));
}

export function observationOperatorNames(obs) {
  if (!obs) return [];
  const names = [];
  (obs.assigned_operators || []).forEach((operator) => {
    const name = (operator && (operator.name || operator.nome)) || "";
    if (name) names.push(String(name).trim());
  });
  if (obs.confirmed_by_name) {
    names.push(String(obs.confirmed_by_name).trim());
  }
  return Array.from(new Set(names.filter(Boolean)));
}

export function observationMachineIdents(obs) {
  if (!obs) return [];
  return machineIdentsFromPlan(obs.service_order_plan);
}

export function observationLandPlotNames(obs) {
  if (!obs) return [];
  return splitNames(obs.land_plot_names);
}

export function observationProductNames(obs) {
  if (!obs) return [];
  const names = productRowsFromPlan(obs.service_order_plan)
    .map((row) => (row && row.name) || "")
    .map((name) => name.trim())
    .filter(Boolean);
  if (obs.products_used) names.push(...splitNames(obs.products_used));
  return Array.from(new Set(names));
}

export function observationTitle(obs) {
  if (!obs) return "";
  return String(obs.operationName || obs.operation_name || obs.description || "").trim();
}

export function uniqueSortedValues(values) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "pt-BR", { sensitivity: "base" })
  );
}

export function collectFilterOptions(observations) {
  const farms = [];
  const operators = [];
  const machines = [];
  const landPlots = [];
  const products = [];
  (observations || []).forEach((obs) => {
    farms.push(...observationFarmNames(obs));
    operators.push(...observationOperatorNames(obs));
    machines.push(...observationMachineIdents(obs));
    landPlots.push(...observationLandPlotNames(obs));
    products.push(...observationProductNames(obs));
  });
  return {
    farms: uniqueSortedValues(farms),
    operators: uniqueSortedValues(operators),
    machines: uniqueSortedValues(machines),
    landPlots: uniqueSortedValues(landPlots),
    products: uniqueSortedValues(products),
  };
}

export function hasActiveFilters(filters) {
  if (!filters) return false;
  return Boolean(
    (filters.query && filters.query.trim()) ||
      filters.operator ||
      filters.machine ||
      filters.farm ||
      selectedLandPlots(filters).length > 0 ||
      filters.dateFrom ||
      filters.dateTo ||
      (filters.product && filters.product.trim()) ||
      (filters.title && filters.title.trim())
  );
}

export function selectedLandPlots(filters) {
  if (!filters) return [];
  if (Array.isArray(filters.landPlots)) {
    return filters.landPlots.map((name) => String(name).trim()).filter(Boolean);
  }
  if (filters.landPlot) return [String(filters.landPlot).trim()].filter(Boolean);
  return [];
}

function matchesAnyLandPlot(names, selected) {
  if (!selected.length) return true;
  const haystack = (names || []).map((name) => String(name).toLowerCase());
  return selected.some((wanted) => {
    const needle = String(wanted).toLowerCase();
    return haystack.some(
      (name) => name === needle || name.includes(needle) || needle.includes(name)
    );
  });
}

function includesText(value, wanted) {
  return String(value || "")
    .toLowerCase()
    .includes(String(wanted || "").trim().toLowerCase());
}

function periodOverlaps(startValue, endValue, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return true;
  const start = new Date(startValue || endValue || 0);
  const end = new Date(endValue || startValue || 0);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    if (end < from) return false;
  }
  if (dateTo) {
    const to = new Date(`${dateTo}T23:59:59`);
    if (start > to) return false;
  }
  return true;
}

export function observationMatchesFilters(obs, filters) {
  if (!obs) return false;
  if (!hasActiveFilters(filters)) return true;

  const query = String((filters && filters.query) || "")
    .trim()
    .toLowerCase();
  if (query) {
    const haystack = [
      obs.operationName,
      obs.operation_name,
      obs.description,
      obs.products_used,
      obs.land_plot_names,
      observationTitle(obs),
      ...observationFarmNames(obs),
      ...observationOperatorNames(obs),
      ...observationMachineIdents(obs),
      ...observationProductNames(obs),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(query)) return false;
  }

  if (filters.operator) {
    const wanted = String(filters.operator).toLowerCase();
    const names = observationOperatorNames(obs).map((name) =>
      name.toLowerCase()
    );
    if (!names.includes(wanted)) return false;
  }

  if (filters.machine) {
    const wanted = String(filters.machine).toLowerCase();
    const idents = observationMachineIdents(obs).map((ident) =>
      ident.toLowerCase()
    );
    if (!idents.includes(wanted)) return false;
  }

  if (filters.farm) {
    const wanted = String(filters.farm).toLowerCase();
    const farms = observationFarmNames(obs).map((name) => name.toLowerCase());
    if (!farms.includes(wanted)) return false;
  }

  const selectedPlots = selectedLandPlots(filters);
  if (
    selectedPlots.length &&
    !matchesAnyLandPlot(observationLandPlotNames(obs), selectedPlots)
  ) {
    return false;
  }

  if (filters.product && filters.product.trim()) {
    const names = observationProductNames(obs);
    if (!names.some((name) => includesText(name, filters.product))) return false;
  }

  if (filters.title && filters.title.trim()) {
    if (!includesText(observationTitle(obs), filters.title)) return false;
  }

  if (
    !periodOverlaps(
      obs.start_date || obs.created,
      obs.end_date || obs.executed_end_date || obs.start_date,
      filters.dateFrom,
      filters.dateTo
    )
  ) {
    return false;
  }

  return true;
}

export function dayReportMatchesFilters(report, filters) {
  if (!report) return false;
  if (!hasActiveFilters(filters)) return true;

  const query = String((filters && filters.query) || "")
    .trim()
    .toLowerCase();
  if (query) {
    const haystack = [
      report.title,
      report.description,
      report.operatorName,
      report.farmName,
      report.landPlotNames,
      report.machineName,
      report.machineIdent,
      report.appliedAmount,
      report.appliedUnit,
      report.products,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(query)) return false;
  }

  if (filters.operator) {
    if (
      String(report.operatorName || "").toLowerCase() !==
      String(filters.operator).toLowerCase()
    ) {
      return false;
    }
  }

  if (filters.machine) {
    const wanted = String(filters.machine).toLowerCase();
    const idents = [report.machineIdent, report.machineName]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    if (!idents.includes(wanted)) return false;
  }

  if (filters.farm) {
    if (!includesText(report.farmName, filters.farm)) return false;
  }

  const selectedPlots = selectedLandPlots(filters);
  if (
    selectedPlots.length &&
    !matchesAnyLandPlot(splitNames(report.landPlotNames), selectedPlots)
  ) {
    return false;
  }

  if (filters.product && filters.product.trim()) {
    const haystack = [
      report.title,
      report.description,
      report.products,
      report.appliedAmount,
    ]
      .filter(Boolean)
      .join(" ");
    if (!includesText(haystack, filters.product)) return false;
  }

  if (filters.title && filters.title.trim()) {
    if (!includesText(report.title, filters.title)) return false;
  }

  if (
    !periodOverlaps(
      report.startedAt || report.timestamp,
      report.endedAt || report.timestamp,
      filters.dateFrom,
      filters.dateTo
    )
  ) {
    return false;
  }

  return true;
}

export const EMPTY_SERVICE_ORDER_FILTERS = {
  query: "",
  operator: "",
  machine: "",
  farm: "",
  landPlots: [],
  dateFrom: "",
  dateTo: "",
  product: "",
  title: "",
};
